import { useRef, useState } from 'react';
import { ATTRIBUTE_TYPES_PATH } from './api';
import { Size, plainLabel, t, unitFamilyLabel } from '@bms2/shared';
import { DetailList, DetailRow } from '../../components/composites';
import { line } from './SettingsShell';
import { Button, IconButton, Select, TextInput } from '../../components/controls';
import { post, useApi } from '../records/api';
import { SettingsAddWindow } from './SettingsAddWindow';
import styles from './settings.module.css';
import { splitValues } from './ValueChips';

export { ATTRIBUTE_TYPES_PATH };

/** One catalog attribute type: its edit, its Retire and its delete (decision 10090). */
export const attributeTypePath = (id: string): string => `${ATTRIBUTE_TYPES_PATH}/${encodeURIComponent(id)}`;

/** One value of an attribute type, deleted where nothing carries it (decision 10090). */
export const attributeTypeValuePath = (id: string, valueId: string): string => `${attributeTypePath(id)}/values/${encodeURIComponent(valueId)}`;

export const ATTRIBUTE_KINDS = ['Measured', 'Enumerated'] as const;

export interface FamilyRow {
  id: string;
  familyName: string;
  units: { id: string; label: string }[];
}

export interface AttributeTypeDraft {
  name: string;
  kind: string;
  familyId: string;
  unitId: string;
  values: string;
}

export const EMPTY_ATTRIBUTE_TYPE: AttributeTypeDraft = { name: '', kind: ATTRIBUTE_KINDS[0], familyId: '', unitId: '', values: '' };

export function attributeTypeReady(draft: AttributeTypeDraft): boolean {
  if (!draft.name.trim()) return false;
  return draft.kind === 'Measured' ? Boolean(draft.familyId && draft.unitId) : splitValues(draft.values).length > 0;
}

export function attributeTypeBody(draft: AttributeTypeDraft) {
  return draft.kind === 'Measured'
    ? { attributeName: draft.name, kind: draft.kind, unitFamilyId: draft.familyId, defaultUnitId: draft.unitId }
    : { attributeName: draft.name, kind: draft.kind, values: splitValues(draft.values) };
}

/**
 * The one form for a catalog attribute type (decision 0285), drawn by Settings ›
 * Catalog › Attribute Types and by the Item's attribute picker, which opens it as
 * a modal when the type wanted is not there yet (product owner, 2026-09-10).
 */
export function AttributeTypeFields({ draft, families, onChange }: { draft: AttributeTypeDraft; families: readonly FamilyRow[]; onChange: (draft: AttributeTypeDraft) => void }) {
  const units = families.find((f) => f.id === draft.familyId)?.units ?? [];
  return (
    <>
      <div className={styles.row}>
        <TextInput size={Size.Medium} label={t('settings.attributeTypes.name')} value={draft.name} onChange={(name) => onChange({ ...draft, name })} type="text" required />
        <Select size={Size.Medium} label={t('settings.attributeTypes.kind')} value={draft.kind} onChange={(kind) => onChange({ ...draft, kind })} options={ATTRIBUTE_KINDS.map((k) => ({ value: k, label: plainLabel('fieldKind', k) }))} dataAttribute={{ 'data-settings-field': 'kind' }} />
      </div>
      {draft.kind === 'Measured' ? (
        <div className={styles.row}>
          <Select size={Size.Medium} label={t('settings.attributeTypes.family')} value={draft.familyId} onChange={(familyId) => onChange({ ...draft, familyId, unitId: '' })} placeholder={t('settings.attributeTypes.choose')} options={families.map((f) => ({ value: f.id, label: unitFamilyLabel(f.familyName) }))} dataAttribute={{ 'data-settings-field': 'family' }} />
          <Select size={Size.Medium} label={t('settings.attributeTypes.defaultUnit')} value={draft.unitId} onChange={(unitId) => onChange({ ...draft, unitId })} disabled={!draft.familyId} placeholder={t('settings.attributeTypes.choose')} options={units.map((u) => ({ value: u.id, label: u.label }))} dataAttribute={{ 'data-settings-field': 'unit' }} />
        </div>
      ) : (
        <div>
          <label className={styles.fieldLabel} htmlFor="at-values">{t('settings.attributeTypes.values')}</label>
          <textarea id="at-values" className={styles.textarea} value={draft.values} onChange={(e) => onChange({ ...draft, values: e.target.value })} data-settings-field="values" />
        </div>
      )}
    </>
  );
}

export interface CreatedAttributeType {
  id: string;
  attributeName: string;
  kind: string;
}

/**
 * The window a catalog attribute type is added in (decision 2803), drawn on the Settings frame by Settings › Catalog › Attribute Types
 * and by the Item's attribute picker, which opens it when the type wanted is not there yet (T3499). The page hands over the unit
 * families it already read; the picker leaves them out and the window reads them. The created row is handed back to be selected.
 */
export function AddAttributeTypeWindow({ apiBaseUrl = '', families, onClose, onCreated }: { apiBaseUrl?: string; families?: readonly FamilyRow[]; onClose: () => void; onCreated: (row: CreatedAttributeType) => void }) {
  const held = useApi<{ rows: unknown[]; families: FamilyRow[] }>(families ? null : ATTRIBUTE_TYPES_PATH, apiBaseUrl);
  const [draft, setDraft] = useState<AttributeTypeDraft>(EMPTY_ATTRIBUTE_TYPE);
  const made = useRef<CreatedAttributeType | null>(null);
  return (
    <SettingsAddWindow
      slug="attribute-type"
      title={t('settings.attributeTypes.addTitle')}
      valid={attributeTypeReady(draft)}
      invalidReason={t('settings.attributeTypes.addNeeds')}
      submit={async () => {
        const answer = await post<CreatedAttributeType>(ATTRIBUTE_TYPES_PATH, attributeTypeBody(draft), apiBaseUrl);
        if (answer.ok) made.current = answer.data;
        return answer.ok ? { ok: true } : answer;
      }}
      onClose={onClose}
      onSaved={() => made.current && onCreated(made.current)}
    >
      <AttributeTypeFields draft={draft} families={families ?? (held.state.kind === 'ready' ? held.state.data.families : [])} onChange={setDraft} />
    </SettingsAddWindow>
  );
}

/** A type as Settings › Catalog › Attribute Types reads it, with the version its Edit window names. */
export interface AttributeTypeRow {
  id: string;
  version: number;
  attributeName: string;
  kind: string;
  isActive: boolean;
  unitFamilyId: string | null;
  family: string | null;
  defaultUnitId: string | null;
  defaultUnit: string | null;
  values: { id: string; value: string; sortOrder: number; isActive: boolean }[];
}

/** The Edit window's draft (decision 10090): the name, the default unit, and the values in their order. */
export interface AttributeTypeEditDraft {
  name: string;
  unitId: string;
  values: { id: string; value: string; isActive: boolean }[];
}

export function editDraftOf(row: AttributeTypeRow): AttributeTypeEditDraft {
  return { name: row.attributeName, unitId: row.defaultUnitId ?? '', values: row.values.map((v) => ({ id: v.id, value: v.value, isActive: v.isActive })) };
}

export function editDraftReady(row: AttributeTypeRow, draft: AttributeTypeEditDraft): boolean {
  if (!draft.name.trim()) return false;
  if (row.kind === 'Measured' && !draft.unitId) return false;
  return draft.values.every((v) => v.value.trim() !== '');
}

/** The PATCH body: every value in its new order, numbered from one, with the version the window read. */
export function editBody(row: AttributeTypeRow, draft: AttributeTypeEditDraft) {
  return {
    version: row.version,
    attributeName: draft.name,
    ...(row.kind === 'Measured' ? { defaultUnitId: draft.unitId } : {}),
    ...(row.kind === 'Enumerated' ? { values: draft.values.map((v, index) => ({ id: v.id, value: v.value, sortOrder: index + 1, isActive: v.isActive })) } : {}),
  };
}

/**
 * The Edit window's fields (decision 10090), Add's shape: the name, the default
 * unit within the type's own family, and for an Enumerated type its values, each
 * renamed, moved, removed or retired. A refusal of a value's removal reads under
 * that value, with Retire beside it.
 */
export function EditAttributeTypeFields({ row, draft, families, refusals, onChange, onRemove }: {
  row: AttributeTypeRow;
  draft: AttributeTypeEditDraft;
  families: readonly FamilyRow[];
  /** The refusal each value's removal met, keyed by the value's id. */
  refusals: ReadonlyMap<string, string>;
  onChange: (draft: AttributeTypeEditDraft) => void;
  onRemove: (value: { id: string; value: string }) => void;
}) {
  const units = families.find((f) => f.id === row.unitFamilyId)?.units ?? [];
  const setValue = (index: number, change: Partial<AttributeTypeEditDraft['values'][number]>) => onChange({ ...draft, values: draft.values.map((v, at) => (at === index ? { ...v, ...change } : v)) });
  const move = (index: number, by: -1 | 1) => {
    const values = [...draft.values];
    const [moved] = values.splice(index, 1);
    values.splice(index + by, 0, moved!);
    onChange({ ...draft, values });
  };
  return (
    <>
      <TextInput size={Size.Medium} label={t('settings.attributeTypes.name')} value={draft.name} onChange={(name) => onChange({ ...draft, name })} type="text" required />
      {row.kind === 'Measured' ? (
        <div>
          <Select size={Size.Medium} label={t('settings.attributeTypes.defaultUnit')} value={draft.unitId} onChange={(unitId) => onChange({ ...draft, unitId })} placeholder={t('settings.attributeTypes.choose')} options={units.map((u) => ({ value: u.id, label: u.label }))} dataAttribute={{ 'data-settings-field': 'unit' }} />
          <p className={styles.hint}>{line(row.family ? unitFamilyLabel(row.family) : null, row.family ? '·' : null, t('settings.attributeTypes.familyFixed'))}</p>
        </div>
      ) : (
        <div data-settings-field="edit-values">
          <span className={styles.fieldLabel}>{t('settings.attributeTypes.valuesHeading')}</span>
          <DetailList look="separator">
            {draft.values.map((v, index) => {
              const refusal = refusals.get(v.id) ?? null;
              return (
                <DetailRow
                  key={v.id}
                  data-attribute-value={v.id}
                  data-active={v.isActive ? 'true' : 'false'}
                  title={<TextInput size={Size.Medium} label={line(t('settings.attributeTypes.value'), index + 1)} labelHidden value={v.value} onChange={(value) => setValue(index, { value })} type="text" required />}
                  badges={v.isActive ? null : <span className={styles.caption}>{t('settings.attributeTypes.valueRetired')}</span>}
                  actions={(
                    <>
                      <IconButton meaning="moveUp" size={Size.Small} label={line(t('settings.attributeTypes.moveUp'), v.value)} onClick={() => move(index, -1)} disabled={index === 0} disabledReason={t('settings.attributeTypes.firstAlready')} name="value-up" />
                      <IconButton meaning="moveDown" size={Size.Small} label={line(t('settings.attributeTypes.moveDown'), v.value)} onClick={() => move(index, 1)} disabled={index === draft.values.length - 1} disabledReason={t('settings.attributeTypes.lastAlready')} name="value-down" />
                      <IconButton meaning="remove" size={Size.Small} label={line(t('settings.attributeTypes.removeValue'), v.value)} onClick={() => onRemove(v)} name="value-remove" />
                      <Button size={Size.Small} text={[v.isActive ? t('settings.retire') : t('settings.reactivate')]} actions={[() => setValue(index, { isActive: !v.isActive })]} />
                    </>
                  )}
                  expansion={refusal ? <span className={styles.caption} role="alert" data-value-refusal={v.id}>{refusal}</span> : null}
                />
              );
            })}
          </DetailList>
        </div>
      )}
    </>
  );
}
